import { JobView, type JobDto } from '@mpr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import {
  buildHandoff,
  checkAttach,
  mergeMediaIds,
  type PublishableMedia,
  type StudioHandoff,
} from '../lib/studioProject.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * "Publish" with two ways to go.
 *
 * A new post opens the composer filled with the text and files; attaching
 * adds the files to a post that is already scheduled. Both are offered from
 * the same button because which one is wanted depends on whether a post for
 * this idea already exists, which only the person knows.
 */
export function PublishMenu({
  projectId,
  message,
  media,
  aspect,
  onNewPost,
  onOpenScheduled,
}: {
  projectId: string | null;
  /** The project's video aspect, when it has one — see StudioHandoff.aspect. */
  aspect?: string | null;
  /** The text for a new post, if there is one. */
  message: string | null;
  /** The files to send. Audio may be present; it is left out of both routes. */
  media: PublishableMedia[];
  onNewPost: (handoff: StudioHandoff) => void;
  onOpenScheduled?: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);

  const handoff = buildHandoff({ projectId, message, media, aspect });
  const canNewPost = handoff.assets.length > 0 || Boolean(handoff.message);
  const canAttach = handoff.assets.length > 0;

  return (
    <div className="publish-menu">
      <button
        type="button"
        className="primary small"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        disabled={!canNewPost}
      >
        <Icon.send />
        {t('studio.publish.button')}
        <Icon.chevron />
      </button>

      {open && (
        <div className="publish-options" role="group">
          <button
            type="button"
            className="publish-option"
            onClick={() => {
              setOpen(false);
              onNewPost(handoff);
            }}
          >
            <strong>{t('studio.publish.newPost')}</strong>
            <span className="muted tiny">{t('studio.publish.newPostHint')}</span>
          </button>
          <button
            type="button"
            className="publish-option"
            disabled={!canAttach}
            onClick={() => {
              setOpen(false);
              setAttaching(true);
            }}
          >
            <strong>{t('studio.publish.attach')}</strong>
            <span className="muted tiny">
              {canAttach ? t('studio.publish.attachHint') : t('studio.publish.attachNeedsMedia')}
            </span>
          </button>
        </div>
      )}

      {attaching && (
        <AttachToScheduledPost
          projectId={projectId}
          media={media}
          onOpenScheduled={onOpenScheduled}
          onClose={() => setAttaching(false)}
        />
      )}
    </div>
  );
}

/** The excerpt a row shows of a post's text. */
function excerpt(text: string | null | undefined): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  return flat.length > 140 ? `${flat.slice(0, 140)}…` : flat;
}

/**
 * The tenant's scheduled posts, each with a button to add these files to it.
 *
 * Only posts the server will let be edited are offered: a scheduled post that
 * republishes somebody else's has no text of ours and no file list to change.
 * What a post may hold is checked here as well, so a refusal is explained
 * beside the row instead of arriving as an error after the click.
 */
function AttachToScheduledPost({
  projectId,
  media,
  onOpenScheduled,
  onClose,
}: {
  projectId: string | null;
  media: PublishableMedia[];
  onOpenScheduled?: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const queryClient = useQueryClient();
  const files = media.filter((m) => m.kind !== 'audio');
  const [done, setDone] = useState<{ recorded: boolean } | null>(null);

  const scheduled = useQuery({
    queryKey: ['jobs', 'attach-picker'],
    queryFn: () => api.jobs({ view: JobView.SCHEDULED }),
  });

  const attach = useMutation({
    mutationFn: async (job: JobDto) => {
      const existing = job.content?.media.map((file) => file.id) ?? [];
      await api.editJob(job.id, {
        mediaAssetIds: mergeMediaIds(
          existing,
          files.map((file) => file.mediaId),
        ),
      });

      // The files are on the post now. Not being able to note that on the
      // project is worth saying, but it must not read as the attach failing.
      let recorded = true;
      if (projectId) {
        try {
          await api.recordGenerationPublication(projectId, {
            publishJobId: job.id,
            mode: 'ATTACHED',
            mediaIds: files.map((file) => file.mediaId),
          });
        } catch {
          recorded = false;
        }
      }
      return { recorded };
    },
    onSuccess: (result) => {
      setDone(result);
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['generation-project'] });
      void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
    },
  });

  const posts = scheduled.data?.jobs ?? [];

  return (
    <div className="card attach-picker">
      <header className="card-head">
        <strong>{t('studio.attach.title')}</strong>
        <button type="button" className="ghost small" onClick={onClose}>
          {t('common.close')}
        </button>
      </header>

      <p className="muted small">{t('studio.attach.videoRule')}</p>

      {done ? (
        <div className="notice ok">
          <p style={{ margin: 0 }}>{t('studio.attach.done')}</p>
          {!done.recorded && <p className="small">{t('studio.attach.notRecorded')}</p>}
          {onOpenScheduled && (
            <div className="actions">
              <button type="button" className="ghost small" onClick={onOpenScheduled}>
                {t('studio.attach.openScheduled')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {scheduled.isLoading && <div className="loading">{t('common.loading')}</div>}
          {scheduled.isError && <ErrorNotice error={scheduled.error} />}
          {scheduled.data && posts.length === 0 && (
            <p className="muted small">{t('studio.attach.none')}</p>
          )}

          <ul className="attach-posts">
            {posts.map((job) => {
              const existing = job.content?.media ?? [];
              const external = job.content?.imageUrls.length ?? 0;
              const fixed = job.draft === null;
              const check = fixed ? null : checkAttach(existing, external, files);
              const blockReason = check && !check.ok ? check.reason : null;

              return (
                <li key={job.id} className="attach-post">
                  <div className="attach-post-body">
                    {job.content?.message ? (
                      <p className="attach-post-text">{excerpt(job.content.message)}</p>
                    ) : (
                      <p className="tiny muted">{t('common.noText')}</p>
                    )}
                    <p className="tiny muted">
                      {job.scheduledAt && <>{t('jobs.goesOut', { date: fmt.dateTime(job.scheduledAt) })} · </>}
                      {t('studio.attach.mediaCount', { count: existing.length + external })}
                    </p>
                    {fixed && <p className="tiny warn">{t('studio.attach.notEditable')}</p>}
                    {blockReason && (
                      <p className="tiny warn">{t(`studio.attach.block.${blockReason}` as TranslationKey)}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="small"
                    disabled={fixed || blockReason !== null || attach.isPending}
                    onClick={() => {
                      if (window.confirm(t('studio.attach.confirm', { count: files.length }))) {
                        attach.mutate(job);
                      }
                    }}
                  >
                    {t('studio.attach.here')}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {attach.isError && (
        <div className="notice error">
          {attach.error instanceof ApiError ? attach.error.message : t('common.requestFailed')}
        </div>
      )}
    </div>
  );
}
